import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { athletesAPI, trainingPreferencesAPI, stravaAPI } from '../api';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import {toast} from "../components/common/Notifier";
import {WeekPlanCard} from "../components/dashboard/WeekPlanCard";
import {formatLastSynced} from "../utils/formatLastSynced";


export const DashboardPage = () => {
  const { t } = useTranslation();
  const { athlete, isGuest } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekPrefs, setWeekPrefs] = useState(null);
  const [syncDays, setSyncDays] = useState('');

  // Strava — состояние для баннера
  const SYNC_RANGE_OPTIONS = [
  { value: '', labelKey: 'preferences.strava_sync_range_auto' },
  { value: '3', labelKey: 'preferences.strava_sync_range_3d' },
  { value: '7', labelKey: 'preferences.strava_sync_range_7d' },
  { value: '14', labelKey: 'preferences.strava_sync_range_14d' },
  { value: '30', labelKey: 'preferences.strava_sync_range_30d' },
];
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaInfo, setStravaInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  const loadStravaStatus = useCallback(async () => {
    try {
      const { data } = await stravaAPI.status();
      setStravaConnected(data.connected);
      setStravaInfo(data);
    } catch (error) {
      setStravaConnected(false);
    }
  }, []);

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      const params = {};
      if (syncDays) {
        const after = new Date();
        after.setDate(after.getDate() - Number(syncDays));
        params.after = after.toISOString().slice(0, 10); // YYYY-MM-DD
      }

      const { data } = await stravaAPI.sync(params);
      if (data.error === 'invalid_token') {
        setSyncError(t('preferences.strava_token_invalid'));
      } else {
        toast.success(t('preferences.strava_synced', { count: data.count }));
        setStravaInfo((prev) => ({ ...prev, last_synced_at: new Date().toISOString() }));
        loadStats();
        setPlanRefreshKey((k) => k + 1);
      }
    } catch (e) {
      setSyncError(t('preferences.strava_sync_failed'));
    } finally {
      setSyncing(false);
    }
  };

  //useCallback для loadStats
  const loadStats = useCallback(async () => {
    try {
      const response = await athletesAPI.stats(athlete.id);
      setStats(response.data);
    } catch (error) {
      if (!isGuest) {
        toast.error(t('error.general'));
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athlete?.id, isGuest]);

  // useCallback для loadWeekPrefs
  const loadWeekPrefs = useCallback(async () => {
    try {
      const response = await trainingPreferencesAPI.get(athlete.id);
      setWeekPrefs(response.data);
    } catch (error) {
      setWeekPrefs(null);
    }
  }, [athlete?.id]);

  useEffect(() => {
    if (athlete && !isGuest) {
      loadStats();
      loadWeekPrefs();
      loadStravaStatus();
    } else {
      setLoading(false);
    }
  }, [athlete, isGuest, loadStats, loadWeekPrefs, loadStravaStatus]);

  // Функция для получения дня недели
  const getWeekdayLabel = (dayIndex) => {
    if (dayIndex === undefined || dayIndex === null) return '—';
    const dayMap = {
      0: 'mon',
      1: 'tue',
      2: 'wed',
      3: 'thu',
      4: 'fri',
      5: 'sat',
      6: 'sun'
    };
    const key = dayMap[dayIndex] || 'mon';
    return t(`preferences.weekday_${key}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

// Гостевой дашборд
if (isGuest) {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-8 text-white">
        <h1 className="text-2xl font-bold text-gray-800">{t('dashboard.guest_welcome')}</h1>
        <p className="mt-2 text-white/80">
          {t('dashboard.guest_message')}
          <button
            onClick={() => window.location.href = '/login'}
            className="ml-2 underline font-semibold hover:text-white"
          >
            {t('common.login')}
          </button>
        </p>
      </div>

      {/* Демо-карточки */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Карточка Moomi the Troll */}
        <div
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all cursor-pointer text-center"
          onClick={() => window.location.href = '/'}
        >
          <div className="flex justify-center mb-3">
            <img
              src="/moom1.png"
              alt="Moomi the Troll"
              className="w-16 h-16 rounded-full object-cover border-2 border-blue-200"
            />
          </div>
          <h3 className="font-semibold">{t('dashboard.demo_moomi')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.demo_moomi_desc')}</p>
          <div className="mt-2 text-blue-600 hover:text-blue-800 text-sm">
            {t('common.go_to')} →
          </div>
        </div>

        {/* Карточка калькулятора */}
        <div
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all cursor-pointer text-center"
          onClick={() => window.location.href = '/calculator'}
        >
          <div className="text-4xl mb-2">🏃</div>
          <h3 className="font-semibold">{t('dashboard.demo_calculator')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.demo_calculator_desc')}</p>
          <div className="mt-2 text-blue-600 hover:text-blue-800 text-sm">
            {t('common.go_to')} →
          </div>
        </div>

        {/* Карточка статистики */}
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-4xl mb-2">📊</div>
          <h3 className="font-semibold">{t('dashboard.demo_stats')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.demo_stats_desc')}</p>
        </div>
      </div>
    </div>
  );
}

 return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-800">
        {t('dashboard.welcome', { name: athlete?.name || t('common.user') })}
      </h1>

      {!athlete?.current_vdot && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="font-semibold text-yellow-800">{t('dashboard.onboarding_needed')}</p>
            <p className="text-sm text-yellow-700">{t('dashboard.onboarding_message')}</p>
          </div>
          <Link
            to="/onboarding"
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ml-4"
          >
            {t('dashboard.onboarding_button')} →
          </Link>
        </div>
      )}

      {/* Week Settings / Weekly Volume / VDOT / Strava — один ряд */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Link
          to="/preferences#week-section"
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow block"
        >
          <div className="text-sm text-gray-500">{t('dashboard.week_settings')}</div>
          {weekPrefs ? (
            <>
              <div className="text-lg font-bold text-indigo-600">
                {t('dashboard.intervals_count', { count: weekPrefs.intervals_per_week })}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {t('dashboard.long_run_day')}: {getWeekdayLabel(weekPrefs.long_run_weekday)}
              </div>
            </>
          ) : (
            <div className="text-gray-400 text-sm">{t('dashboard.no_week_settings')}</div>
          )}
        </Link>

        <Link to="/preferences#weekly-volume-section" className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
          <div className="text-sm text-gray-500">{t('profile.weekly_volume')}</div>
          <div className="text-2xl font-bold text-purple-600">
            {athlete?.weekly_volume_km || '—'} {t('common.km')}
          </div>
        </Link>

        <Link to="/preferences#hr-max-section" className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
          <div className="text-sm text-gray-500">❤️ {t('profile.effective_hr_max')}</div>
          <div className="text-2xl font-bold text-red-600">
            {athlete?.effective_max_hr || '—'}
          </div>
          {athlete?.easy_hr_cap_range && (
            <div className="text-xl font-bold text-blue-600">
              {t('profile.easy_hr_cap')}: {athlete.easy_hr_cap_range.low}–{athlete.easy_hr_cap_range.high}
            </div>
          )}
        </Link>

        <Link to="/preferences#vdot-section" className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
          <div className="text-sm text-gray-500">{t('profile.current_vdot')}</div>
          <div className="text-2xl font-bold text-blue-600">
            {athlete?.current_vdot || '—'}
          </div>
        </Link>
      {stravaConnected ? (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex flex-col justify-between">
          <div>
            <div className="text-sm font-semibold text-orange-800 flex items-center gap-1">
              <img src="/strava.png" alt="Strava" className="w-4 h-4" /> Strava
            </div>
            {stravaInfo?.last_synced_at && (
              <div className="text-xs text-gray-500 mt-1">
                {t("preferences.strava_last_synced")}: {formatLastSynced(stravaInfo.last_synced_at, t)}
              </div>
            )}
            {syncError && <div className="text-xs text-red-500 mt-1">{syncError}</div>}
          </div>

          <div className="mt-2 flex items-center gap-2">
            <select
              value={syncDays}
              onChange={(e) => setSyncDays(e.target.value)}
              disabled={syncing}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white flex-1"
            >
              {SYNC_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
            <button
              onClick={handleSyncNow}
              disabled={syncing}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap"
            >
              {syncing ? t("preferences.strava_syncing") : t("preferences.strava_sync_now_button")}
            </button>
          </div>
        </div>
      ) : (
          <Link to="/preferences#strava-section" className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow flex flex-col justify-center items-center text-center">
            <div className="text-sm text-gray-500"><img src="/strava.png" alt="Strava" className="w-4 h-4" /> Strava</div>
            <div className="text-xs text-gray-400 mt-1">{t("preferences.strava_desc")}</div>
          </Link>
        )}
      </div>

      {athlete && <WeekPlanCard athleteId={athlete.id} refreshTrigger={planRefreshKey} />}


    </div>
  );
};
